import { captureAttributionFromUrl, attributionDataLayerParams } from "./attribution";

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

export type ProductEvent = "select_plan" | "begin_checkout" | "generate_lead";

export interface PageContext {
  page_type: string;
  content_group: string;
}

export function getPageContext(pathname: string): PageContext {
  const p = pathname.replace(/\/+$/, "") || "/";

  if (p === "/") return { page_type: "landing", content_group: "pro" };
  if (p === "/lifetime") return { page_type: "landing", content_group: "lifetime" };
  if (p === "/diagnostico/resultado") return { page_type: "diagnostic_result", content_group: "diagnostic" };
  if (p === "/diagnostico") return { page_type: "diagnostic_landing", content_group: "diagnostic" };
  if (p === "/parceiros") return { page_type: "partners", content_group: "partners" };
  if (p === "/politicas") return { page_type: "legal", content_group: "legal" };
  if (p === "/success") return { page_type: "checkout_result", content_group: "success" };
  if (p === "/cancel") return { page_type: "checkout_result", content_group: "cancel" };

  return { page_type: "other", content_group: "other" };
}

function push(payload: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(payload);
}

function gtagPush(...args: unknown[]) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(args as unknown as Record<string, unknown>);
}

function parseGaClientId(gaCookieValue: string): string | null {
  const parts = gaCookieValue.split(".");
  if (parts.length < 4) return null;
  return `${parts[2]}.${parts[3]}`;
}

let clickDelegationArmed = false;

export const analytics = {
  init() {
    if (typeof window === "undefined") return;

    this.setConsentDefault();
    if (this.getConsent() === "granted") this.grantConsent();

    const gtmId = import.meta.env.VITE_GTM_ID || "GTM-NL4RQXV4";
    if (gtmId && !document.querySelector('script[src*="googletagmanager.com/gtm.js"]')) {
      window.dataLayer = window.dataLayer ?? [];
      window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });

      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
      document.head.appendChild(script);

      const noscript = document.createElement("noscript");
      const iframe = document.createElement("iframe");
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
      iframe.height = "0";
      iframe.width = "0";
      iframe.style.display = "none";
      iframe.style.visibility = "hidden";
      noscript.appendChild(iframe);
      document.body.insertBefore(noscript, document.body.firstChild);
    }
  },

  trackPageView(pagePath: string, pageTitle: string) {
    const ctx = getPageContext(pagePath);
    const attribution = captureAttributionFromUrl();
    push({
      event: "page_view",
      page_path: pagePath,
      page_title: pageTitle,
      page_location: typeof window !== "undefined" ? window.location.href : pagePath,
      ...ctx,
      ...attributionDataLayerParams(attribution)
    });
  },

  trackEvent(eventName: string, params?: Record<string, unknown>) {
    push({ event: eventName, ...(params ?? {}) });
  },

  trackProductEvent(event: ProductEvent, params?: Record<string, unknown>) {
    this.trackEvent(event, params ?? {});
  },

  setConsentDefault() {
    gtagPush("consent", "default", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      wait_for_update: 500
    });
  },

  grantConsent() {
    gtagPush("consent", "update", {
      analytics_storage: "granted",
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted"
    });
    try {
      localStorage.setItem("vw_consent", "granted");
    } catch {
      // ignore
    }
  },

  denyConsent() {
    try {
      localStorage.setItem("vw_consent", "denied");
    } catch {
      // ignore
    }
  },

  getConsent(): "granted" | "denied" | null {
    try {
      const v = localStorage.getItem("vw_consent");
      return v === "granted" || v === "denied" ? v : null;
    } catch {
      return null;
    }
  },

  resetConsent() {
    try {
      localStorage.removeItem("vw_consent");
    } catch {
      // ignore
    }
  },

  getClientId(): string | null {
    if (typeof document === "undefined") return null;
    const m = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
    return m ? parseGaClientId(decodeURIComponent(m[1])) : null;
  },

  initClickDelegation() {
    if (typeof document === "undefined" || clickDelegationArmed) return;
    clickDelegationArmed = true;

    document.addEventListener("click", (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target) return;

      const cta = target.closest("button, a[href]");
      if (cta) {
        const el = cta as HTMLElement;
        const label = el.textContent?.trim() || el.getAttribute("aria-label") || "icon-button";
        analytics.trackEvent("click_cta", {
          cta_text: label.slice(0, 120),
          cta_variant: cta.tagName === "A" ? "link" : "button",
          cta_href: cta.getAttribute("href") || undefined,
          location: window.location.pathname
        });
      }
    });
  }
};
